import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    Button, 
    IconButton,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,     
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from 'state/axios';
import { getFlagUrl } from 'state/helpers';
import FlexBetween from 'components/FlexBetween'; 
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const CrewListDialog = ({ 
    open, 
    onClose, 
    invoiceId, 
    selectedFlights, 
    crewMembers = [], 
    onCrewListSaved
}) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);  
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const crewRanksData = useSelector((state) => state.crewRanks);

    const [cabinCrewRanks, setCabinCrewRanks] = useState([]);
    const [crewList, setCrewList] = useState([]);
    let ordinal = 1;

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";  
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);

    const getTime = (flight, timeMode, field) => 
        timeMode === 'UTC' ? flight[field] : flight[`${field}LocalBase`];
    
    
    useEffect(() => {
        const filteredRanks = crewRanksData
            .filter(rank => rank.rankType === 'Cabin Crew')   
            .map(rank => rank.rankCode)      
            
        setCabinCrewRanks(filteredRanks);
        
    }, [crewRanksData]);
    
    useEffect(() => {  
        const fetchCrewMembers = async (flightDate, flightDesc, dep) => {
            try {
                const url = '/legmembers/legmembersbyflightdesc';
                const payload = { flightDate, flightDesc, dep };
                
                const response = await api.post(url, payload);
    
                const { legMembers } = response.data;
                
                const crews = legMembers.filter(member => 
                    cabinCrewRanks.includes(member.post) && !member.pax
                );
                
                return crews;
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch crew members!");
                return [];
            }
        };

        const loadCrewList = async () => {
            if (crewMembers.length > 0) {
                setCrewList(crewMembers);
            } else {
                try {                    
                    if (selectedFlights.length > 0) {
                        const { 
                            flightDate, 
                            flightNo, 
                            flightDesc, 
                            dep 
                        } = selectedFlights[0];
                                                
                        const crews = await fetchCrewMembers(
                            flightDate, 
                            flightDesc || `QH${flightNo}`, 
                            dep
                        );
                        setCrewList(crews);
                    }
                } catch (error) {
                    toast.error(error || "Failed to fetch crew list!");
                }
            }
        };
    
        loadCrewList();
    }, [cabinCrewRanks, crewMembers, selectedFlights]);

    const handleDeleteCrew = (idx) => {
        setCrewList((prev) => prev.filter((_, index) => index !== idx));
    };

    const handleSubmit = async () => {        
        try {
            await api.patch(`/inflightsales/updatecrewmembers/${invoiceId}`, 
                { 
                    invoiceId,
                    crewMembers: crewList,
                    userId: user._id, 
                },
            );

            toast.success("Crew list updated successfully!");
            onCrewListSaved(crewList);
            onClose();
        } catch (error) {            
            toast.error(error.response?.data?.message || "Failed to update crew list!");
        }
    };


    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '90%' : '50%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween pb={1} borderBottom={`1px solid ${neutralDark}`}>
                    <Typography 
                        variant="h6" 
                        color={primaryDark} 
                        fontWeight="bold" 
                    >
                        CREW LIST
                    </Typography>
                    <Box onClick={() => dispatch(setTimeMode())}>                            
                        <img
                            alt="Flag"
                            width={25}
                            height={25}                          
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>      
                <Box mt={1}>
                    {selectedFlights.map((flight, index) => (                       
                        <FlexBetween key={index}>
                            <Typography variant="h6" color={primaryDark}>{flight.flightNo}</Typography>
                            <Typography variant="h6">
                                {timeMode === 'UTC' 
                                    ? `${format(flight.flightDate, "ddMMMyyyy")}`
                                    : `${format(flight.flightDateLocalBase, "ddMMMyyyy")}`                                    
                                }
                            </Typography>
                            <Typography variant="h6">
                                {getTime(flight, timeMode, 'etd')}                  
                            </Typography>
                            <Typography variant="h6">{flight.dep}</Typography>
                            <Typography variant="h6">{flight.arr}</Typography>
                            <Typography variant="h6">
                                {getTime(flight, timeMode, 'eta')}                  
                            </Typography>
                        </FlexBetween>                   
                    ))}
                </Box>                               
            </DialogTitle>

            <DialogContent>
                {crewList && crewList.map((crew, idx) => (
                    <FlexBetween key={idx}>             
                        <FlexBetween gap={0.5}>
                            <Typography width="20px" align='right' color={primaryDark}>
                                {ordinal++}. 
                            </Typography>
                            <Typography color={primaryDark}>
                                {crew.crewName  || 'Unknown'}
                            </Typography>
                        </FlexBetween>                   
                        
                        <FlexBetween gap={0.5}>
                            <Typography width="35px" textAlign="center">{crew.crewCode || ''}</Typography>
                            <Typography width="30px" textAlign="center">{crew.post || ''}</Typography>
                            <Typography width="30px" textAlign="center">{crew.base || ''}</Typography>
                            <IconButton
                                aria-label="Delete Crew Member"
                                color="secondary"                                
                                onClick={() => handleDeleteCrew(idx)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </FlexBetween>
                    </FlexBetween>
                ))}
            </DialogContent>
            <DialogActions>          
                <Button 
                    variant="outlined" 
                    onClick={handleSubmit}
                >
                    Submit
                </Button>          
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={onClose}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CrewListDialog;
